// src/pages/SetupPage.tsx

import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Button,
  Link
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  ContentCopy,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const CodeBlock = styled(Paper)(({ theme }) => ({
  background: '#1e1e1e',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '4px',
  padding: theme.spacing(1.5, 2),
  fontFamily: 'Monaco, Consolas, "Courier New", monospace',
  fontSize: '0.875rem',
  color: '#d4d4d4',
  whiteSpace: 'pre-wrap',
  overflowX: 'auto',
  wordBreak: 'break-all',
  flexGrow: 1,
}));

const InstructionCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  backgroundColor: 'rgba(20, 20, 20, 0.8)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: theme.spacing(2),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const ActionButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 4),
  borderRadius: theme.spacing(1.5),
  fontWeight: 500,
  fontSize: '1.1rem',
  textTransform: 'none',
  color: 'white',
  background: 'linear-gradient(45deg, #667eea, #764ba2)',
  '&:hover': {
    background: 'linear-gradient(45deg, #5a71d5, #6a4292)',
    boxShadow: '0 5px 15px rgba(102, 126, 234, 0.4)',
  },
  marginTop: theme.spacing(4),
}));

const faqs = [
  {
    question: "Where does my ad-account data live?",
    answer: "Your ad-account data remains securely within your ad accounts. Plex only requests read-only access and does not store your data."
  },
  {
    question: "What can I do with it today?",
    answer: "You can perform deep analysis, generate reports, get optimization suggestions, and ask complex questions about your campaign performance directly within Claude."
  },
  {
    question: "Can it actually change budgets or duplicate ad-sets for me?",
    answer: "Currently, the integration has read-only access, so it cannot make changes to your campaigns. Its primary function is for analysis and insights."
  }
];

const Step = ({ number, children }: { number: number, children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        backgroundColor: '#667eea',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold',
        flexShrink: 0,
        mr: 2,
      }}
    >
      {number}
    </Box>
    <Box sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '1.1rem' }}>
      {children}
    </Box>
  </Box>
);

const SetupPage: React.FC = () => {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const integrationUrl = 'https://api.plexhq.ai/mcp';

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(integrationUrl);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };
  
  return (
    <Box sx={{ height: '100%' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              color: '#ffffff',
              fontWeight: 700,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              mb: 1,
            }}
          >
            Setup Instructions
          </Typography>
          <Typography
            variant="h5"
            component="h2"
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontWeight: 300,
              mb: 4,
            }}
          >
            Connect Plex to Claude AI (Pro/Max)
          </Typography>
        </Box>

        {/* Setup Method Section */}
        <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
          <InstructionCard elevation={0}>
            <Step number={1}>
              Open <Link href="https://claude.ai/settings/integrations" target="_blank" rel="noopener" sx={{ color: '#87CEEB', fontWeight: 'bold' }}>claude.ai/settings/integrations</Link> in your browser.
            </Step>
            <Step number={2}>
              Add Integration Name: <strong>Plex</strong>
            </Step>
            <Step number={3}>
              <Box sx={{ width: '100%' }}>
                <Typography sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.9)' }}>Add Integration URL:</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CodeBlock elevation={0}>{integrationUrl}</CodeBlock>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ContentCopy fontSize="small" />}
                    onClick={handleCopy}
                    sx={{
                      color: isCopied ? '#4caf50' : 'rgba(255, 255, 255, 0.7)',
                      borderColor: isCopied ? '#4caf50' : 'rgba(255, 255, 255, 0.3)',
                      textTransform: 'none',
                      flexShrink: 0,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderColor: isCopied ? '#4caf50' : 'white',
                      }
                    }}
                  >
                    {isCopied ? 'Copied' : 'Copy URL'}
                  </Button>
                </Box>
              </Box>
            </Step>
            <Step number={4}>
              Click "Add" and then "Connect".
            </Step>
            <Step number={5}>
  You will be redirected to <strong>app.plexhq.ai</strong> to log in with your Google Account.
</Step>
            <Step number={6}>
              Connect your Ad Accounts in "Integrations".
            </Step>
            <Step number={7}>
              Start using <strong>@Plex</strong> in Claude.
            </Step>

            <Link
  href="https://claude.ai/settings/integrations"
  target="_blank"
  rel="noopener"
  sx={{
    textDecoration: 'none',
    alignSelf: 'center'
  }}
>
  <ActionButton>
    Go to Claude Integrations
  </ActionButton>
</Link>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', textAlign: 'center', mt: 1 }}>
              Requires Claude Paid Subscription
            </Typography>
          </InstructionCard>
        </Box>

        {/* FAQ Section */}
        <Box sx={{ maxWidth: '900px', mx: 'auto', mt: 8 }}>
          <Typography variant="h4" sx={{ textAlign: 'center', fontWeight: 600, mb: 4 }}>
            Frequently Asked Questions
          </Typography>
          {faqs.map((faq, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Button
                fullWidth
                onClick={() => toggleFaq(index)}
                endIcon={expandedFaq === index ? <ExpandLess /> : <ExpandMore />}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  textAlign: 'left',
                  p: 2,
                  color: 'white',
                  textTransform: 'none',
                  backgroundColor: 'rgba(20, 20, 20, 0.8)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(30, 30, 30, 0.9)',
                  }
                }}
              >
                <Typography sx={{ fontWeight: 500 }}>{faq.question}</Typography>
              </Button>
              {expandedFaq === index && (
                <Paper elevation={0} sx={{ p: 3, border: '1px solid rgba(255, 255, 255, 0.1)', borderTop: 'none', backgroundColor: 'rgba(20, 20, 20, 0.5)' }}>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    {faq.answer}
                  </Typography>
                </Paper>
              )}
            </Box>
          ))}
        </Box>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 6, pt: 4 }}>
           <Divider sx={{ mb: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            Need help? Contact us through our Slack community or at support@plexhq.ai.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default SetupPage;
